/**
 * API Response Handler Utility
 * 
 * Provides centralized handling of API responses with automatic
 * toast notifications based on backend messages.
 */

/**
 * Extracts the message from a backend response
 * Supports multiple response formats:
 * - { success: true, message: "...", data: {...} }
 * - { success: false, message: "...", error: "..." }
 * - { message: "..." }
 * - { error: "..." }
 * 
 * @param {Object} response - The response object from the backend
 * @param {string} fallbackMessage - Fallback message if none found in response
 * @returns {string} The extracted message
 */
export const extractMessage = (response, fallbackMessage = '') => {
  if (response && typeof response === 'object') {
    // Try to get message from response
    if (response.message) return response.message;
    if (response.error) return response.error;
    if (response.data && response.data.message) return response.data.message;
  }
  
  return fallbackMessage;
};

/**
 * Handles API response with automatic toast notifications
 * 
 * @param {Object} response - The response object from the backend
 * @param {Object} notification - The notification hook object with showSuccess/showError methods
 * @param {Object} options - Configuration options
 * @param {string} options.successMessage - Fallback success message
 * @param {string} options.errorMessage - Fallback error message
 * @param {boolean} options.showSuccessToast - Whether to show success toast (default: true)
 * @param {boolean} options.showErrorToast - Whether to show error toast (default: true)
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 * @returns {Object} The response data
 */
export const handleApiResponse = (response, notification, options = {}) => {
  const {
    successMessage = 'Operation completed successfully',
    errorMessage = 'Operation failed',
    showSuccessToast = true,
    showErrorToast = true,
    onSuccess,
    onError
  } = options;

  // Check if response indicates success
  const isSuccess = response?.success !== false;

  if (isSuccess) {
    // Extract success message from backend or use fallback
    const message = extractMessage(response, successMessage);
    
    if (showSuccessToast && notification?.showSuccess) {
      notification.showSuccess(message);
    }
    
    if (onSuccess) {
      onSuccess(response);
    }
  } else {
    // Extract error message from backend or use fallback
    const message = extractMessage(response, errorMessage);
    
    if (showErrorToast && notification?.showError) {
      notification.showError(message);
    }
    
    if (onError) {
      onError(response);
    }
  }

  return response;
};

/**
 * Handles API errors (exceptions) with automatic toast notifications
 * 
 * @param {Error} error - The error object
 * @param {Object} notification - The notification hook object
 * @param {string} fallbackMessage - Fallback error message
 * @returns {never} Throws the error after handling
 */
export const handleApiError = (error, notification, fallbackMessage = 'An error occurred') => {
  const message = error?.message || error?.error || fallbackMessage;
  
  if (notification?.showError) {
    notification.showError(message);
  }
  
  console.error('API Error:', error);
  throw error;
};

/**
 * Higher-order function to wrap API calls with automatic response handling
 * 
 * @param {Function} apiCall - The API function to call
 * @param {Object} notification - The notification hook object
 * @param {Object} options - Configuration options (same as handleApiResponse)
 * @returns {Function} Wrapped API call function
 * 
 * @example
 * const deleteWithNotification = withApiNotification(
 *   () => DashboardService.deleteBranch(id),
 *   { showSuccess, showError },
 *   { successMessage: 'Branch deleted successfully' }
 * );
 * await deleteWithNotification();
 */
export const withApiNotification = (apiCall, notification, options = {}) => {
  return async (...args) => {
    try {
      const response = await apiCall(...args);
      return handleApiResponse(response, notification, options);
    } catch (error) {
      handleApiError(error, notification, options.errorMessage);
    }
  };
};

/**
 * Custom hook-like wrapper for use in React components
 * Provides methods to call API with automatic notifications
 * 
 * @param {Object} notification - The notification hook object
 * @returns {Object} Object with helper methods
 * 
 * @example
 * const { callApi } = useApiHandler(useNotification());
 * 
 * await callApi(
 *   () => DashboardService.deleteBranch(id),
 *   {
 *     successMessage: 'Branch deleted successfully',
 *     onSuccess: () => loadBranches()
 *   }
 * );
 */
export const useApiHandler = (notification) => {
  const callApi = async (apiCall, options = {}) => {
    try {
      const response = await apiCall();
      return handleApiResponse(response, notification, options);
    } catch (error) {
      handleApiError(error, notification, options.errorMessage);
    }
  };

  return { callApi };
};



